import dayjs from "dayjs";
import "dayjs/locale/ru";
import type { Payment } from "@/models/personModel";

export interface LoanScheduleItem {
    month: string;
    date: string;
    monthlyPayment: number;
    remaining: number;
}

export interface CalculateLoanDetailsResult {
    total: number;
    monthlyPayment: number;
    closingDate: string;
    schedule: LoanScheduleItem[];
    netProfit: number;
    remainingDebt: number;
    paidByClient: number;
    discountApplied: number;
    settledAmount: number;
}

export function calculateLoanDetails(
    phonePrice: number,
    downPayment: number,
    term: number,
    percent: number,
    creditDate: string,
    productionCost: number,
    payments: Payment[] = [],
    earlyClosed = false,
    earlyCloseDate: string | null = null,
    status?: string,
    selectedOption?: string | null
): CalculateLoanDetailsResult {
    const safePhonePrice = Math.max(0, Number(phonePrice || 0));
    const safeDownPayment = Math.max(0, Number(downPayment || 0));
    const safeTerm = Math.max(0, Number(term || 0));
    const safePercent = Math.max(0, Number(percent || 0));
    const safeProductionCost = Math.max(0, Number(productionCost || 0));

    const paidByClient = (payments || [])
        .filter((p) => p.type !== "discount")
        .reduce((sum, p) => sum + Math.max(0, Number(p.amount || 0)), 0);

    const discountApplied = (payments || [])
        .filter((p) => p.type === "discount")
        .reduce((sum, p) => sum + Math.max(0, Number(p.amount || 0)), 0);

    const settledAmount = paidByClient + discountApplied;

    const isSpecialClosed = status === "closedEarly" || status === "returned";

    if (isSpecialClosed) {
        const isReturn = status === "returned" || selectedOption === "return";
        const total = isReturn ? safeDownPayment : safePhonePrice;
        const remainingDebt = 0;
        const monthlyPayment = 0;
        const netProfit = isReturn
            ? Math.max(0, safeDownPayment - discountApplied)
            : Math.max(0, total - safeProductionCost - discountApplied);

        return {
            total: Number(total.toFixed(2)),
            monthlyPayment: Number(monthlyPayment.toFixed(2)),
            closingDate: earlyCloseDate || "",
            schedule: [],
            netProfit: Number(netProfit.toFixed(2)),
            remainingDebt: Number(remainingDebt.toFixed(2)),
            paidByClient: Number(paidByClient.toFixed(2)),
            discountApplied: Number(discountApplied.toFixed(2)),
            settledAmount: Number(settledAmount.toFixed(2)),
        };
    }

    const creditBody = Math.max(0, safePhonePrice - safeDownPayment);
    const totalAfterPercent = creditBody * (1 + safePercent / 100);
    const total = totalAfterPercent + safeDownPayment;
    const monthlyPayment = safeTerm > 0 ? totalAfterPercent / safeTerm : 0;
    const remainingDebt = Math.max(0, total - (safeDownPayment + settledAmount));
    const netProfit = Math.max(0, total - safeProductionCost - discountApplied);

    const closingDate =
        creditDate && safeTerm
            ? dayjs(creditDate).locale("ru").add(safeTerm, "month").format("YYYY-MM-DD")
            : "";

    const schedule: LoanScheduleItem[] =
        creditDate && safeTerm > 0
            ? Array.from({ length: safeTerm }, (_, i) => {
                const paymentDate = dayjs(creditDate)
                    .locale("ru")
                    .add(i + 1, "month");

                const remainingAfterPayment = Math.max(
                    0,
                    total - safeDownPayment - monthlyPayment * (i + 1)
                );

                return {
                    month: `${i + 1}`,
                    date: paymentDate.format("YYYY-MM-DD"),
                    monthlyPayment: Number(monthlyPayment.toFixed(2)),
                    remaining: Number(remainingAfterPayment.toFixed(2)),
                };
            })
            : [];

    return {
        total: Number(total.toFixed(2)),
        monthlyPayment: Number(monthlyPayment.toFixed(2)),
        closingDate,
        schedule,
        netProfit: Number(netProfit.toFixed(2)),
        remainingDebt: Number(remainingDebt.toFixed(2)),
        paidByClient: Number(paidByClient.toFixed(2)),
        discountApplied: Number(discountApplied.toFixed(2)),
        settledAmount: Number(settledAmount.toFixed(2)),
    };
}